import { DataSourceSortableState } from "client/Data/sorting";
import { isNil } from "client/Misc/utils";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { never } from "client_legacy/ndaw/errors";
import { newGuid } from "client_legacy/ndaw/guid";
import { HubItemCategoryModel, HubItemHubSidebarNodeModel } from "client/Api/Hub/hub";
import { ActiveFilter, HubItemIsNewFilter } from "../common";
import { KnowledgeItemPreviewItemModel } from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import { Details } from "./Details/Details";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { CategoryOverview } from "./CategoryOverview/CategoryOverview";
import { GroupOverview } from "./GroupOverview/GroupOverview";
import { defaultSortingOption } from "./common";
import { KnowledgeItemClickableBreadcrumbSegment } from "./useKnowledgeItemBreadcrumb";
import { HubItemViewLocation } from "client/Api/Hub/common.details";

export type KnowledgeItemsView = {
	kind: "categoryOverView";
} | {
	kind: "groupOverview";
	groupId: number | null;
} | {
	kind: "details";
	id: number;
	commentId?: number;
};

export type KnowledgeItemsPageContentProps = {
	view: KnowledgeItemsView;
	onViewChange: (view: KnowledgeItemsView) => void;
	hierarchy: readonly HubItemHubSidebarNodeModel[];
	selectedCategoryId: number | null;
	onActiveFilterChange: (activeFilter: ActiveFilter | null) => void;
	activeFilter: ActiveFilter | null;
	onSelectCategory: (categoryId: number | "all" | null) => void;
	categories: readonly HubItemCategoryModel[];
	onHierarchyChanged: () => void;
	hubItemKindsCanCreateNewCategoriesFor: ReadonlyArray<HubItemKind>;
	onItemCategoryChange: (id: OptionalModel<number | null>) => void;
	canOpenHubManagement: boolean;
};

export const KnowledgeItemsPageContent = (props: KnowledgeItemsPageContentProps): ReactElement => {
	const {
		view,
		onViewChange,
		hierarchy,
		selectedCategoryId,
		onSelectCategory,
		categories,
		onHierarchyChanged,
		hubItemKindsCanCreateNewCategoriesFor,
		activeFilter,
		onActiveFilterChange,
		onItemCategoryChange,
		canOpenHubManagement
	} = props;

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		value: "",
		version: newGuid()
	});

	const [sorting, setSorting] = useState<DataSourceSortableState<KnowledgeItemPreviewItemModel>>(defaultSortingOption.value);
	const [isNewFilter, setIsNewFilter] = useState<HubItemIsNewFilter>("all");

	const hasCreatePermission: boolean = useMemo(
		() => {
			const canCreateNewCategory: boolean = hubItemKindsCanCreateNewCategoriesFor.includes(HubItemKind.KnowledgeItem);

			return canCreateNewCategory
				|| categories.some(x => x.hubItemKind === HubItemKind.KnowledgeItem && x.canCreateItem);
		},
		[
			categories,
			hubItemKindsCanCreateNewCategoriesFor
		]
	);

	const navigateToCategoryHandler = useCallback(
		(categoryId?: number | null): void => {
			onViewChange({ kind: "categoryOverView" });

			if (categoryId !== undefined) {
				onSelectCategory(categoryId);
			}
		},
		[onSelectCategory, onViewChange]
	);

	const navigateToGroupHandler = useCallback(
		(groupId: number | null): void => {
			onViewChange({
				kind: "groupOverview",
				groupId
			});
		},
		[onViewChange]
	);

	const activeFilterChangedHandler = useCallback(
		(filter: ActiveFilter | null): void => {
			onActiveFilterChange(filter);

			if (filter !== null) {
				onSelectCategory("all");
			}
		},
		[onSelectCategory, onActiveFilterChange]
	);

	const getCategory = useCallback(
		(categoryId: number | nil): HubItemCategoryModel | undefined => {
			if (categories === null || isNil(categoryId)) {
				return undefined;
			}

			return categories.find(c => c.id === categoryId);
		},
		[categories]
	);

	const openDetailsHandler = useCallback(
		(id: number): void => {
			onViewChange({
				kind: "details",
				id
			});
		},
		[onViewChange]
	);

	const breadcrumbNavigationHandler = useCallback(
		(segment: KnowledgeItemClickableBreadcrumbSegment): void => {
			switch(segment.kind) {
				case "overview":
					navigateToGroupHandler(null);
					break;
				case "group":
					navigateToGroupHandler(segment.id);
					break;
				case "category":
					navigateToCategoryHandler(segment.id);
					break;
				default:
					never(segment);
			}
		},
		[
			navigateToCategoryHandler,
			navigateToGroupHandler
		]
	);

	switch (view.kind) {
		case "groupOverview":
			return (
				<GroupOverview
					groupId={view.groupId}
					hierarchy={hierarchy}
					onNavigateToGroup={navigateToGroupHandler}
					hasCreatePermission={hasCreatePermission}
					canOpenHubManagement={canOpenHubManagement}
					onSelectCategory={onSelectCategory}
					onHierarchyChanged={onHierarchyChanged}
					activeFilter={activeFilter}
					onClearActiveFilter={() => onActiveFilterChange(null)}
					onBreadcrumbNavigation={breadcrumbNavigationHandler}
				/>
			);
		case "categoryOverView":
			return (
				<CategoryOverview
					hierarchy={hierarchy}
					activeFilter={activeFilter}
					onActiveFilterChange={activeFilterChangedHandler}
					selectedCategoryId={selectedCategoryId}
					onSelectedCategoryIdChange={onSelectCategory}
					categories={categories}
					getCategory={getCategory}
					onCategoryCreated={onHierarchyChanged}
					onNavigateToItem={openDetailsHandler}
					searchTerm={searchTerm}
					onSearchTermChange={setSearchTerm}
					sorting={sorting}
					onSortingChange={setSorting}
					hasCreatePermission={hasCreatePermission}
					onBreadcrumbNavigation={breadcrumbNavigationHandler}
					canOpenHubManagement={canOpenHubManagement}
					isNewFilter={isNewFilter}
					onIsNewFilterChange={setIsNewFilter}
				/>
			);
		case "details":
			return (
				<Details
					mode="full"
					id={view.id}
					hierarchy={hierarchy}
					selectedCategoryId={selectedCategoryId}
					onSelectCategory={navigateToCategoryHandler}
					activeFilter={activeFilter}
					onActiveFilterChange={activeFilterChangedHandler}
					searchTerm={searchTerm}
					sorting={sorting}
					getCategory={getCategory}
					onBack={navigateToCategoryHandler}
					onNavigateToItem={openDetailsHandler}
					onCategoryCreated={onHierarchyChanged}
					onBreadcrumbNavigation={breadcrumbNavigationHandler}
					onItemCategoryChange={onItemCategoryChange}
					isNewFilter={isNewFilter}
					viewLocation={HubItemViewLocation.Hub}
					deepLink={view.commentId
						? {
							kind: "comment",
							commentId: view.commentId
						}
						: undefined
					}
				/>
			);
		default:
			never(view);
	}
};
KnowledgeItemsPageContent.displayName = "KnowledgeItemsPageContent";